import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingFarmResourceConsumption,
  useMutateFootprintingFarmResourceConsumption,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin } from "components";

interface FarmWasteProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const FarmWasteForm = ({ primaryDataId, readOnly, farmId, year }: FarmWasteProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [wasteKgs, setWasteKgs] = useState<number | null>(null);
  const [wasteWaterM3, setWasteWaterM3] = useState<number | null>(null);

  const farmResourceConsumptionQuery = useGetFootprintingFarmResourceConsumption(primaryDataId ?? "");
  const { mutate: mutateFarmResourceConsumption } = useMutateFootprintingFarmResourceConsumption(
    farmId,
    year,
    {
      id: farmResourceConsumptionQuery.data?.id,
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      wasteKgs,
      wasteWaterM3,
    },
    primaryDataId
  );

  const isLoading = queryIsLoading(farmResourceConsumptionQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFarmResourceConsumption();
      }
    },
    [isLoading, mutateFarmResourceConsumption, readOnly]
  );

  useEffect(() => {
    if (farmResourceConsumptionQuery.data) {
      setWasteKgs(farmResourceConsumptionQuery.data.wasteKgs ?? null);
      setWasteWaterM3(farmResourceConsumptionQuery.data.wasteWaterM3 ?? null);

      formRef.current?.setFieldsValue({
        wasteKgs: farmResourceConsumptionQuery.data.wasteKgs ?? null,
        wasteWaterM3: farmResourceConsumptionQuery.data.wasteWaterM3 ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["wasteKgs", "wasteWaterM3"] as const).some((field) => farmResourceConsumptionQuery.data[field] !== null);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [farmResourceConsumptionQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("wasteForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("wasteForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-farm-waste-form" className="margin-bottom-100 padding-200">
        <Form ref={formRef}>
          <Form.Item className="padding-bottom-100">
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-waste-input"
              disabled={readOnly}
              name="wasteKgs"
              label={t("wasteForm.wasteQuestionLabels")}
              tooltip={t("wasteForm.tooltipWasteQuestionLabels")}
              suffix="kg"
              value={wasteKgs}
              initialValue={wasteKgs}
              onChange={(v) => setWasteKgs(v)}
              rules={[
                { required: true, message: "" }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("wasteForm.wasteQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
          <Form.Item className="padding-bottom-100">
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-waste-water-input"
              disabled={readOnly}
              name="wasteWaterM3"
              label={t("wasteForm.wasteWaterQuestionLabel")}
              tooltip={t("wasteForm.tooltipWasteWaterQuestionLabels")}
              suffix="m³"
              value={wasteWaterM3}
              initialValue={wasteWaterM3}
              onChange={(v) => setWasteWaterM3(v)}
              rules={[
                { required: true, message: "" }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("wasteForm.wasteQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
